/**
 * Mask annotation extension handler.
 *
 * Masks store one NIfTI file per label instance. The storage_path goes into
 * annotation_files with file_type='mask_nifti'. Label name/value go on the
 * annotation row itself.
 */
import { BaseExtensionHandler } from "../_base.ts"
import type { ExtensionManifest, SerializedAnnotation, StoredAnnotation } from "../_interface.ts"
import { createMaskSchema, maskConfigSchema, updateMaskSchema } from "./schema.ts"

export class MaskHandler extends BaseExtensionHandler {
  readonly type = "mask"
  readonly manifest: ExtensionManifest = {
    type: "mask",
    label: "Mask Segmentation",
    scope: "series",
    storage: "file",
    supportsLinking: false
  }

  getConfigSchema() {
    return maskConfigSchema
  }

  getCreateSchema() {
    return createMaskSchema
  }

  getUpdateSchema() {
    return updateMaskSchema
  }

  serializeForDb(apiData: unknown): SerializedAnnotation {
    const data = apiData as {
      label_name?: string
      label_value?: number
      storage_path?: string
    }

    return {
      annotation: {
        label_name: data.label_name ?? null,
        label_value: data.label_value ?? null
      },
      data: [],
      files: data.storage_path ? [{ file_type: "mask_nifti", storage_path: data.storage_path }] : []
    }
  }

  deserializeFromDb(stored: StoredAnnotation): unknown {
    const maskFile = stored.files.find((f) => f.file_type === "mask_nifti")
    return {
      id: stored.annotation.id,
      submission_id: stored.annotation.submission_id,
      project_module_id: stored.annotation.project_module_id,
      series_id: stored.annotation.series_id,
      label_name: stored.annotation.label_name,
      label_value: stored.annotation.label_value,
      storage_path: maskFile?.storage_path ?? null,
      created_at: stored.annotation.created_at,
      updated_at: stored.annotation.updated_at
    }
  }

  getExportColumns(_templateSchema: unknown): string[] {
    return ["mask_storage_paths"]
  }

  toExportRow(annotations: StoredAnnotation[], _templateSchema: unknown): Record<string, string | number> {
    const paths = annotations
      .map((a) => {
        const file = a.files.find((f) => f.file_type === "mask_nifti")
        return `${a.annotation.label_name}:${file?.storage_path ?? ""}`
      })
      .join("; ")

    return { mask_storage_paths: paths || "" }
  }
}
